import {
  FormattedNumberDiv,
  LoadingAnimation,
  Wizard,
} from '@odmonorepo/core-components';
import { FullConfig } from '../../Features/OfferConfiguration/Components/FullConfig/FullConfig';
import SimpleConfig from '../../Features/OfferConfiguration/Components/SimpleConfig/SimpleConfig';
import PPDOptions from '../../Features/OfferConfiguration/Components/PPD/PPDOptions';
import { Card, Row, Stack } from 'react-bootstrap';
import { ConfigStepCard } from '../../Features/OfferConfiguration/Cards/ConfigStepCard';
import * as yup from 'yup';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { maxLoanAmount } from '../../Features/OfferConfiguration/minMaxLoanAmounts';
import { useDetailedOffers } from 'merchant-api/offer';
import { PricedDetails } from 'merchant-api/types';
import { atom, useAtom } from 'jotai';
import { merchantPortalText, offerStateRegs } from 'merchant-components';
import { AppError } from 'merchant-api';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const schema = yup.object().shape({
  offerId: yup.string(),
  paymentFrequency: yup.string(),
  loanAmount: yup.number(),
});

export const offerConfigSteps = {
  SIMPLE_CONFIG: <ConfigStepCard><SimpleConfig /></ConfigStepCard>,
  FULL_CONFIG: <ConfigStepCard><FullConfig /></ConfigStepCard>,
  PPD_OPTION: <ConfigStepCard><PPDOptions /></ConfigStepCard>,
};

type StepKey = keyof typeof offerConfigSteps;

const offerConfigStepOrder: StepKey[] = ['SIMPLE_CONFIG', 'FULL_CONFIG', 'PPD_OPTION'];

const OfferConfigForm = ({ defaultValues }: { defaultValues: any }) => {
  const form = useForm({ resolver: yupResolver(schema), defaultValues });
  return (
    <FormProvider {...form}>
      <Wizard
        steps={offerConfigSteps}
        stepOrder={offerConfigStepOrder}
        showProgressBar={false}
        onStepChange={() => {
          form.clearErrors();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </FormProvider>
  );
};

export const pricedDetails = atom<PricedDetails>()
export const disablePreview = atom(false)
export const remainingBalance = atom(0)

export const OfferConfig = () => {
  const { tlOffers, remainingPrincipal, customerData, isLoadingDetailedOffers, isErrorOffers } = useDetailedOffers();
  const stateRegsStates = ['CA', 'NY'];
  const useStateRegLanguage = stateRegsStates.includes(customerData?.businessState);
  const [pricing, setPriced] = useAtom(pricedDetails)
  const [remaining, setRemaining] = useAtom(remainingBalance)

  if (isLoadingDetailedOffers) return <Stack><LoadingAnimation className="mx-auto mt-7"/></Stack>
  if (isErrorOffers) throw new Error('Error loading offers');
  if (!tlOffers || tlOffers.length === 0) throw AppError.NoOffer;

  if (tlOffers) {
    !pricing && setPriced({...tlOffers[0].pricedDetails, ppd: tlOffers[0].prePaymentDiscountPct === 100 ? 100 : 25})
    !remaining && remainingPrincipal && setRemaining(remainingPrincipal)
  }

  const defaultValues = {
    offer: {
      offerId: tlOffers[0].offerId,
      loanAmount: tlOffers[0].pricedDetails.lineAmount,
      paymentFrequency: tlOffers[0].pricedDetails.paymentFrequency,
    }
  };

  return (
    <IntlProvider locale="en" messages={useStateRegLanguage ? offerStateRegs : merchantPortalText}>
      <Stack gap={5}>
        <Row>
        <h3 className="text-secondary">
          <FormattedNumberDiv
            value={maxLoanAmount(tlOffers)}
            style={'currency'}
            currency="USD"
            minimumFractionDigits={0}
            prefix={<FormattedMessage id="offerConfig.title.prefix" />}
            suffix="."
          />
        </h3>
        {remainingPrincipal && remainingPrincipal > 0 && (
          <FormattedNumberDiv
            value={remainingPrincipal}
            style="currency"
            currency="USD"
            className='fs-5 pt-2 pb-2'
            minimumFractionDigits={0}
            prefix={<FormattedMessage id="offerConfig.renewal.offer.increase.prefix" />}
            suffix={<FormattedMessage id="offerConfig.renewal.offer.increase.suffix" />}
          />
        )}
        </Row>
        <Card>
          <OfferConfigForm defaultValues={defaultValues} />
        </Card>
      </Stack>
    </IntlProvider>
  );
};

export default OfferConfig;
